/**
 * Created: 2026-04-07
 * Initial commit: Add court category and create-event dialog (feat/event/mr-9)
 *
 * Dialog for creating an event from selected OPEN slots in SlotsPage.
 * Adapted from tc-mini create-event page.
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Field, type ISchema, NumberField, useForm } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import NumberInput from '../components/NumberInput'
import { CreateEventDocument, EventCategory } from '../gql/graphql'

// Created: 2026-04-07 | Initial commit: Add court category and create-event dialog
gql`
  mutation CreateEvent($eventInput: CreateEventInput!) {
    createEvent(eventInput: $eventInput) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

type EventFormValues = {
  name: string
  location: string
  description: string
  fee: number
  requiredRating: number
  drawSize: number
}

interface CreateEventDialogProps {
  open: boolean
  slots: { courtId: string; courtName: string; startTime: string }[]
  onClose: () => void
  onSuccess: () => void
}

/** Cell-style row: label on the left, input on the right. */
function CellRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  )
}

export default function CreateEventDialog({
  open,
  slots,
  onClose,
  onSuccess,
}: CreateEventDialogProps) {
  const isMultiCourt = new Set(slots.map((s) => s.courtId)).size > 1
  const [eventType, setEventType] = useState<EventCategory>(EventCategory.Freeplay)

  const schema = useMemo((): ISchema => {
    const drawSizeRules: string[] = ['min:2', 'max:16']
    if (eventType === EventCategory.Tournament) drawSizeRules.push('in:4,5,8')
    return {
      rules: {
        name: 'required',
        location: 'required',
        fee: 'required',
        drawSize: drawSizeRules,
      },
      messages: {
        'required.name': '请填写活动名称',
        'required.location': '请填写活动地点',
        'required.fee': '请填写报名费',
        'in.drawSize': '比赛签位只能为4、5 或 8',
        'min.drawSize': '签位须在2-16 之间',
        'max.drawSize': '签位须在2-16 之间',
      },
    }
  }, [eventType])

  const initialLocation = useMemo(() => {
    const seen = new Set<string>()
    return slots
      .map((s) => s.courtName)
      .filter((name) => name && !seen.has(name) && seen.add(name))
      .join(' · ')
  }, [slots])

  const courtName = useMemo(() => initialLocation, [initialLocation])

  const form = useForm<EventFormValues>(
    {
      name: '',
      location: initialLocation,
      description: '',
      fee: 50,
      requiredRating: 2.0,
      drawSize: 4,
    },
    schema,
  )

  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // When the dialog opens, force Tournament if multi-court
  useEffect(() => {
    if (open && isMultiCourt) setEventType(EventCategory.Tournament)
  }, [open, isMultiCourt])

  const [createEvent] = useMutation(CreateEventDocument)

  // Derived from slots
  const startTime = useMemo(() => {
    if (slots.length === 0) return null
    return slots.reduce(
      (min, s) => (s.startTime < min ? s.startTime : min),
      slots[0].startTime,
    )
  }, [slots])

  // Duration = number of unique start times (not total slots across courts)
  const duration = useMemo(() => new Set(slots.map((s) => s.startTime)).size, [slots])

  const startTimeFormatted = startTime
    ? format(new Date(startTime), 'yyyy-MM-dd HH:mm')
    : ''
  const endTimeFormatted = startTime
    ? format(new Date(new Date(startTime).getTime() + duration * 3600_000), 'HH:mm')
    : ''

  const handleClose = () => {
    form.reset()
    setEventType(isMultiCourt ? EventCategory.Tournament : EventCategory.Freeplay)
    setSubmitError('')
    onClose()
  }

  const handleSubmit = async () => {
    if (form.validate(false)) return

    setSubmitting(true)
    try {
      const { name, location, description, fee, requiredRating, drawSize } = form.values
      const result = await createEvent({
        variables: {
          eventInput: {
            eventType,
            name: (name ?? '').trim(),
            location: (location ?? '').trim(),
            drawSize: drawSize ?? 8,
            requiredRating,
            fee,
            description: (description ?? '').trim() || null,
            slots: slots.map((s) => ({ courtId: s.courtId, startTime: s.startTime })),
          },
        },
      })
      const data = result.data?.createEvent
      if (data?.__typename === 'GraphqlRequestError') {
        setSubmitError(data.message ?? '')
        return
      }
      onSuccess()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        创建活动
        {courtName && (
          <Typography variant="body2" color="text.secondary">
            {courtName} · {startTimeFormatted}–{endTimeFormatted}（{duration} 小时）
          </Typography>
        )}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1}>
          <FormControl>
            <FormLabel>活动类型</FormLabel>
            <RadioGroup
              row
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventCategory)}
            >
              <FormControlLabel
                value={EventCategory.Freeplay}
                control={<Radio />}
                label="畅打"
                disabled={isMultiCourt}
              />
              <FormControlLabel
                value={EventCategory.Tournament}
                control={<Radio />}
                label="比赛"
              />
            </RadioGroup>
          </FormControl>

          <Field form={form} name="name" label="活动名称" required fullWidth />

          <Field form={form} name="location" label="活动地点" required fullWidth />

          <CellRow label="签位 *">
            <Field
              form={form}
              name="drawSize"
              sx={{ width: 150 }}
              component={NumberField}
              componentProps={{
                inputProps: { min: 2, max: 16, decimalScale: 0 },
              }}
            />
          </CellRow>

          <CellRow label="等级要求（1-7）">
            <Field
              form={form}
              name="requiredRating"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: { max: 7, decimalScale: 1, fixedDecimalScale: false },
                },
              }}
            />
          </CellRow>

          <CellRow label="报名费（元）*">
            <Field
              form={form}
              name="fee"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: { max: 999, decimalScale: 2, fixedDecimalScale: true },
                },
              }}
            />
          </CellRow>

          <Field
            form={form}
            name="description"
            label="活动说明（可选）"
            required={false}
            fullWidth
            multiline
            rows={3}
            helperText={`${(form.values.description ?? '').length}/200`}
          />

          {submitError && (
            <Typography color="error" variant="body2">
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          提交
        </Button>
      </DialogActions>
    </Dialog>
  )
}
